/**
 * EarningsShared.jsx — earnings/EarningsShared.jsx
 *
 * Small presentational components reused by both EarningsTab and VolAnalysisTab.
 * Kept in one file since they're tightly coupled to the earnings feature.
 */

import { getToken, getModelPref as _getModelPref } from '../../lib/api';

// ── API base URL ─────────────────────────────────────────────────────────────
// In dev: Vite proxies /api → Worker on :8787
// In prod: calls the standalone Worker directly
export const WORKER_BASE = import.meta.env.PROD
  ? 'https://options-dashboard-api.ladiida.workers.dev/api'
  : '/api';

export function authHeaders() {
  return { 'Content-Type': 'application/json', 'x-session-token': getToken() };
}

// ── API calls — KV-backed shared cache ────────────────────────────────────────
// Cache lives in Cloudflare KV (shared across all users).
// First sync of the week calls Haiku; subsequent calls return cached result.
// force=true bypasses cache for a fresh Haiku call.

export async function callAIEndpoint(endpoint, userMessage, force = false) {
  const model = _getModelPref();
  const r = await fetch(`${WORKER_BASE}/${endpoint}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ userMessage, model, force }),
  });
  const ct = r.headers.get('content-type') || '';
  if (!r.ok || !ct.includes('application/json')) {
    const txt = await r.text();
    throw new Error(`API error ${r.status}: ${txt.slice(0, 200)}`);
  }
  const d = await r.json();
  if (d.error) throw new Error(typeof d.error === 'object' ? d.error.message : d.error);
  return d; // already parsed — Worker returns the parsed JSON directly
}

export async function fetchCacheStatus() {
  const r = await fetch(`${WORKER_BASE}/cache/status`, { headers: authHeaders() });
  if (!r.ok) return null;
  return r.json();
}

export async function invalidateCache(keys) {
  const r = await fetch(`${WORKER_BASE}/cache/invalidate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ keys }),
  });
  return r.json();
}

// Legacy localStorage cache — kept as offline fallback only
const LS_TTL = 8 * 60 * 60 * 1000; // 8h
export function loadLocalCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > LS_TTL) return null; // expired
    return { data, ts, stale: false, localOnly: true };
  } catch { return null; }
}
export function saveLocalCache(key, data) {
  try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

/**
 * Fetch this week's real earnings calendar from Yahoo Finance via the Worker.
 * Returns { tickers: [...], weekRange: "...", fetchedAt: ... }
 */
export async function fetchEarningsCalendar() {
  const r = await fetch(`${WORKER_BASE}/earnings-calendar`, { headers: authHeaders() });
  if (!r.ok) return null;
  return r.json();
}

// ── Formatters ────────────────────────────────────────────────────────────────
export const fmt    = (n, d = 2) => n == null ? '—' : Number(n).toFixed(d);
export const fmtPct = n => n == null ? '—' : `${n >= 0 ? '+' : ''}${fmt(n)}%`;

// ── Presentational components ─────────────────────────────────────────────────

/** Horizontal bar showing volatility score 0-100. Red = high, green = low. */
export function VolBar({ score, t }) {
  const color = score >= 75 ? t.down : score >= 50 ? t.warn : t.up;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 5, background: t.border, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${score}%`, background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontFamily: 'monospace', fontSize: 11, color, fontWeight: 600, minWidth: 22 }}>{score}</span>
    </div>
  );
}

/** Split bull/bear probability bar. */
export function ProbBar({ probUp, t }) {
  const dn = 100 - probUp;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 10, color: t.up, fontFamily: 'monospace', minWidth: 30 }}>{probUp}%↑</span>
      <div style={{ flex: 1, height: 6, borderRadius: 3, overflow: 'hidden', display: 'flex' }}>
        <div style={{ width: `${probUp}%`, background: t.up }} />
        <div style={{ width: `${dn}%`, background: t.down }} />
      </div>
      <span style={{ fontSize: 10, color: t.down, fontFamily: 'monospace', minWidth: 30, textAlign: 'right' }}>{dn}%↓</span>
    </div>
  );
}

/** Colour-coded call/put skew label. */
export function SkewBadge({ skew, t }) {
  const map = {
    'strong call bias': { bg: t.upSoft,   fg: t.up,    lbl: '↑↑ calls' },
    'slight call bias': { bg: t.upSoft,   fg: t.up,    lbl: '↑ calls'  },
    neutral:            { bg: t.tagBg,    fg: t.muted, lbl: 'neutral'  },
    'slight put bias':  { bg: t.downSoft, fg: t.down,  lbl: '↓ puts'   },
    'strong put bias':  { bg: t.downSoft, fg: t.down,  lbl: '↓↓ puts'  },
  };
  const c = map[skew] || { bg: t.tagBg, fg: t.muted, lbl: skew || '—' };
  return (
    <span style={{ background: c.bg, color: c.fg, fontSize: 10, padding: '2px 7px', borderRadius: 3, fontWeight: 700, fontFamily: 'monospace' }}>
      {c.lbl}
    </span>
  );
}

/** Sync button + cache freshness indicator strip. */
export function CacheBar({ cacheTs, cacheStale, weekRange, onSync, syncing, label, shared }) {
  const age = cacheTs ? (() => {
    const mins = Math.round((Date.now() - cacheTs) / 60000);
    return mins < 60 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`;
  })() : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
      <button
        onClick={onSync} disabled={syncing}
        style={{
          background: syncing ? 'var(--bg-hover)' : 'var(--accent-soft)',
          color: 'var(--accent)', border: '1px solid var(--accent-soft)',
          padding: '6px 14px', borderRadius: 5, fontSize: 11, fontWeight: 700,
          letterSpacing: '.08em', textTransform: 'uppercase',
          cursor: syncing ? 'not-allowed' : 'pointer', opacity: syncing ? .6 : 1,
        }}
      >
        {syncing ? '⟳ Loading…' : `⟳ Refresh ${label}`}
      </button>

      <span style={{ fontSize: 11, color: 'var(--faint)', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {age ? (
          <>
            <span style={{
              width: 7, height: 7, borderRadius: '50%', display: 'inline-block',
              background: cacheStale ? 'var(--warn)' : 'var(--up)',
              boxShadow: cacheStale ? 'none' : '0 0 6px var(--up)',
            }} />
            {cacheStale ? `stale · ${age}` : `cached · ${age}`}
            {weekRange && <span style={{ color: 'var(--muted)' }}>· {weekRange}</span>}
            {shared && (
              <span style={{ fontSize: 9, background: 'var(--accent-soft)', color: 'var(--accent)', padding: '1px 6px', borderRadius: 3, letterSpacing: '.06em', fontWeight: 700 }}>
                ☁ SHARED CACHE
              </span>
            )}
          </>
        ) : syncing ? null : (
          <span>Checking cache…</span>
        )}
      </span>
    </div>
  );
}

/** Shown before the first sync. */
export function EmptyState({ label, onSync, syncing }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--faint)' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
      <div style={{ fontSize: 14, marginBottom: 6 }}>No {label} data loaded</div>
      <div style={{ fontSize: 12 }}>
        Click <strong style={{ color: 'var(--accent)' }}>Sync {label}</strong> to fetch via AI
      </div>
      <div style={{ fontSize: 11, marginTop: 4 }}>~$0.001 per sync · Claude Haiku</div>
      <button
        onClick={onSync} disabled={syncing}
        style={{
          marginTop: 16, background: 'var(--accent-soft)', color: 'var(--accent)',
          border: '1px solid var(--accent-soft)', padding: '8px 20px', borderRadius: 5,
          fontSize: 12, fontWeight: 700, cursor: syncing ? 'not-allowed' : 'pointer',
        }}
      >
        {syncing ? '⟳ Loading…' : `⟳ Sync ${label}`}
      </button>
    </div>
  );
}

/** Animated dots while Haiku is responding. */
export function Spinner() {
  return (
    <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--muted)', fontSize: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 12 }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)',
            animation: `pulse 1.2s ease-in-out ${i * .2}s infinite`,
          }} />
        ))}
      </div>
      <style>{`@keyframes pulse{0%,80%,100%{opacity:.2;transform:scale(.8)}40%{opacity:1;transform:scale(1)}}`}</style>
      Fetching from Claude Haiku…
    </div>
  );
}

/** Inline error with optional retry button. */
export function ErrMsg({ msg, onRetry }) {
  return (
    <div style={{ background: 'var(--down-soft)', border: '1px solid var(--down)', borderRadius: 6, padding: '10px 14px', color: 'var(--down)', fontSize: 12, marginBottom: 12 }}>
      ⚠ {msg}
      {onRetry && (
        <button onClick={onRetry} style={{ marginLeft: 10, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>
          Retry
        </button>
      )}
    </div>
  );
}
